import React, { createContext, useContext, useState, useRef, useEffect } from 'react'
import {
    /* Hooks */
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl } from 'react-intl'

/* Helpers */
import { getStoreHoursData } from '../utils/storelocator-utils'

export const StoreLocatorContext = createContext()

export const StoreLocatorProvider = ({ children }) => {
    /* Utils */
    const intl = useIntl()

    /* State */
    const [map, setMap] = useState(null)
    const [stores, setStores] = useState([])
    const [searchValue, setSearchValue] = useState(null)
    const [latitude, setLatitude] = useState(null)
    const [longitude, setLongitude] = useState(null)
    const [permissionStatus, setPermissionStatus] = useState(null)
    const [geolocationLoading, setGeolocationLoading] = useState(true)
    const [noSearchResults, setNoSearchResults] = useState(false)
    const [isGeolocationSearch, setIsGeolocationSearch] = useState(false)
    const [selectedStore, setSelectedStore] = useState(null)
    const [activeMarkerId, setActiveMarkerId] = useState(null)
    const [toggleStoreInfo, setToggleStoreInfo] = useState(false)
    const [isStoreDataLoading, setIsStoreDataLoading] = useState(false)

    /* Refs */
    const storeCardRefs = useRef(new Map())

    /* Hooks */
    const { isOpen: isOpenStoreDetails, onClose: onCloseStoreDetails, onOpen: onOpenStoreDetails } = useDisclosure()

    /* Methods */
    const setStoreCardRef = (id) => (element) => {
        if (element) {
            storeCardRefs.current.set(id, element)
        } else {
            storeCardRefs.current.delete(id)
        }
    }

    const getLocation = () => {
        if (!navigator.geolocation) {
            console.error('❌ Geolocation is not supported by this browser')
            setGeolocationLoading(false)
            setPermissionStatus('denied')
            return
        }

        setGeolocationLoading(true)

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setSearchValue(null)
                setLatitude(position.coords.latitude)
                setLongitude(position.coords.longitude)
                setGeolocationLoading(false)
                setIsGeolocationSearch(true)
                setPermissionStatus('granted')
            },
            (error) => {
                console.error('❌ Geolocation error:', error)
                setGeolocationLoading(false)
                setPermissionStatus('denied')
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
            }
        )
    }

    const getLocalizedHoursStatus = (store) => {
        const hoursData = getStoreHoursData(store)

        switch (hoursData.status) {
            case 'OPEN_UNTIL':
                return {
                    status: hoursData.status,
                    message: intl.formatMessage(
                        { id: 'store_locator.hours.open_until', defaultMessage: 'Open until {time}' },
                        { time: hoursData.time }
                    )
                }
            case 'OPEN_AT':
                return {
                    status: hoursData.status,
                    message: intl.formatMessage(
                        { id: 'store_locator.hours.open_at', defaultMessage: 'Open at {time}' },
                        { time: hoursData.time }
                    )
                }
            case 'OPEN_TOMORROW':
                return {
                    status: hoursData.status,
                    message: intl.formatMessage(
                        { id: 'store_locator.hours.open_tomorrow', defaultMessage: 'Open tomorrow from {openTime} - {closeTime}' },
                        { openTime: hoursData.openTime, closeTime: hoursData.closeTime }
                    )
                }
            case 'OPEN_NEXT_DAY':
                return {
                    status: hoursData.status,
                    message: intl.formatMessage(
                        { id: 'store_locator.hours.open_next_day', defaultMessage: 'Open {day} from {openTime} - {closeTime}' },
                        { day: hoursData.day, openTime: hoursData.openTime, closeTime: hoursData.closeTime }
                    )
                }
            default:
                return {
                    status: 'NOT_AVAILABLE',
                    message: intl.formatMessage(
                        { id: 'store_locator.hours.not_available', defaultMessage: 'Hours not available' }
                    )
                }
        }
    }

    /* Hooks */
    useEffect(() => {
        // Functionality to toggle store card border based on store selection
        const arrayOfRefs = Array.from(storeCardRefs.current.entries()).map(([key, value]) => ({
            id: key,
            value: value
        }))

        if (arrayOfRefs.length === 0) {
            return
        }

        const currentIndex = arrayOfRefs.findIndex(ref => ref?.id === selectedStore?.id)
        const currentCardRef = arrayOfRefs[currentIndex]
        const prevCardRef = arrayOfRefs[currentIndex - 1]

        // Reset all border cards
        arrayOfRefs.forEach((ref) => {
            if (ref.value) {
                ref.value.style.borderColor = 'black';
            }
        })

        // Set transparent border to current card
        if (currentCardRef && currentCardRef.value) {
            currentCardRef.value.style.borderColor = 'transparent';

            currentCardRef.value.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });
        }

        // Set transparent border to prev card
        if (prevCardRef && prevCardRef.value) {
            prevCardRef.value.style.borderColor = 'transparent';
        }
    }, [selectedStore])


    return (
        <StoreLocatorContext.Provider
            value={{
                map,
                setMap,
                stores,
                setStores,
                searchValue,
                setSearchValue,
                latitude,
                setLatitude,
                longitude,
                setLongitude,
                permissionStatus,
                setPermissionStatus,
                geolocationLoading,
                setGeolocationLoading,
                getLocation,
                noSearchResults,
                setNoSearchResults,
                activeMarkerId,
                setActiveMarkerId,
                selectedStore,
                setSelectedStore,
                isOpenStoreDetails,
                onOpenStoreDetails,
                onCloseStoreDetails,
                toggleStoreInfo,
                setToggleStoreInfo,
                getLocalizedHoursStatus,
                isStoreDataLoading,
                setIsStoreDataLoading,
                isGeolocationSearch,
                setIsGeolocationSearch,
                storeCardRefs,
                setStoreCardRef
            }}
        >
            {children}
        </StoreLocatorContext.Provider>
    )
}

export const useStoreLocator = () => useContext(StoreLocatorContext);
